import React, { useEffect, useState, useContext, createContext } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft, ArrowUpRight, ExternalLink, Check, Quote, ImageIcon, X,
  Store, Wand2, LayoutDashboard, Coins, CreditCard, Truck, Star, ShieldCheck,
  Palette, Ruler, Calculator, SplitSquareHorizontal, TrendingUp, Gift, Tag,
  Mail, Send, UserCircle, Crown, Users, Globe, Receipt, Landmark,
  ClipboardList, Package, Boxes, Headphones, Bot, BarChart3, SlidersHorizontal,
  Code2, FlaskConical, Server,
} from 'lucide-react';
import '@/styles/casestudy.css';
import useReveal from '@/hooks/useReveal';
import { useSeo } from '@/lib/seo';
import { CaseStudyNav } from '@/components/CaseStudyNav';
import RelatedJournal from '@/components/RelatedJournal';
import { ScrollProgress } from '@/components/ScrollProgress';
import { BackToTop } from '@/components/BackToTop';
import {
  BOOST_LOGO, LDS_HERO, LDS_OG_IMAGE, LDS_NAV, LDS_CHALLENGE, LDS_GLANCE, LDS_SECTIONS,
  LDS_INTEGRATIONS, LDS_ENGINEERING, LDS_RESULTS, LDS_CTA,
} from '@/data/londonDrumsticksCaseStudy';

const LightboxContext = createContext(null);

const CANONICAL = 'https://boostmarketing.agency/case-studies/london-drumstick-co';
const SEO_JSONLD = [
  {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: 'London Drumstick Co. — a direct-to-drummer store, custom stick builder and full back-office',
    description:
      'How Boost! designed and built a bespoke e-commerce and business-operations platform for London Drumstick Co. — storefront, made-to-order stick configurator, loyalty, an endorsed-artist programme and a complete admin back-office.',
    image: LDS_OG_IMAGE,
    author: { '@type': 'Organization', name: 'BOOST! Design & Marketing', url: 'https://boostmarketing.agency/' },
    publisher: { '@type': 'Organization', name: 'BOOST! Design & Marketing', logo: { '@type': 'ImageObject', url: BOOST_LOGO } },
    about: { '@type': 'Organization', name: 'London Drumstick Co.', url: 'https://www.londondrumsticks.co.uk' },
    keywords: 'bespoke ecommerce, product configurator, custom drumsticks, fulfilment automation, loyalty engine, CRM, international shipping, Stripe, full-stack build',
    mainEntityOfPage: { '@type': 'WebPage', '@id': CANONICAL },
    inLanguage: 'en-GB',
  },
  {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://boostmarketing.agency/' },
      { '@type': 'ListItem', position: 2, name: 'Work', item: 'https://boostmarketing.agency/work' },
      { '@type': 'ListItem', position: 3, name: 'London Drumstick Co.', item: CANONICAL },
    ],
  },
];

const ICONS = {
  Store, Wand2, LayoutDashboard, Coins, CreditCard, Truck, Star, ShieldCheck,
  Palette, Ruler, Calculator, SplitSquareHorizontal, TrendingUp, Gift, Tag,
  Mail, Send, UserCircle, Crown, Users, Globe, Receipt, Landmark,
  ClipboardList, Package, Boxes, Headphones, Bot, BarChart3, SlidersHorizontal,
  Code2, FlaskConical, Server,
};

function Reveal({ children, className = '', delay = 0, as: Tag = 'div', ...rest }) {
  const ref = useReveal();
  return (
    <Tag ref={ref} className={`cs-reveal ${className}`} style={{ transitionDelay: `${delay}ms` }} {...rest}>
      {children}
    </Tag>
  );
}

function Hero() {
  return (
    <header className="cs-hero" data-testid="lds-hero" id="top">
      <div className="cs-container">
        <Reveal className="cs-hero-eyebrow" as="span"><span className="cs-dot" /> {LDS_HERO.eyebrow}</Reveal>
        <Reveal>
          <h1 className="cs-hero-title" data-testid="lds-hero-title">
            London <span className="cs-hl">Drumstick Co.</span>
          </h1>
        </Reveal>
        <Reveal delay={80}><p className="cs-hero-sub">{LDS_HERO.positioning}</p></Reveal>
        <Reveal delay={140} className="cs-hero-meta">
          <div className="cs-chips">
            {LDS_HERO.chips.map((c) => <span className="cs-chip" key={c}>{c}</span>)}
          </div>
          <span className="cs-year">{LDS_HERO.year}</span>
        </Reveal>
        <Reveal delay={180}>
          <a className="cs-hero-live" href={LDS_HERO.liveUrl} target="_blank" rel="noopener noreferrer" data-testid="lds-hero-live-link">
            <span className="cs-client-tag">Client:</span> {LDS_HERO.client}
            <ExternalLink size={16} />
          </a>
        </Reveal>
        <Reveal delay={120} className="cs-hero-figure">
          <div className="cs-frame">
            <div className="cs-frame-bar">
              <i /><i /><i />
              <span className="cs-frame-url">londondrumsticks.co.uk</span>
            </div>
            <img src={LDS_HERO.image} alt="London Drumstick Co. — cinematic storefront on desktop and phone" loading="eager" />
          </div>
        </Reveal>
      </div>
    </header>
  );
}

function SectionNav() {
  const [active, setActive] = useState(LDS_NAV[0].id);
  useEffect(() => {
    const ids = LDS_NAV.map((n) => n.id);
    const obs = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        if (visible[0]) setActive(visible[0].target.id);
      },
      { rootMargin: '-45% 0px -50% 0px', threshold: [0, 0.25, 0.5, 1] },
    );
    ids.forEach((id) => { const el = document.getElementById(id); if (el) obs.observe(el); });
    return () => obs.disconnect();
  }, []);
  const go = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };
  return (
    <nav className="cs-secnav" data-testid="lds-section-nav" aria-label="Case study sections">
      <div className="cs-container cs-secnav-inner">
        {LDS_NAV.map((n) => (
          <button
            key={n.id}
            type="button"
            className={`cs-secnav-link ${active === n.id ? 'cs-secnav-link-active' : ''}`}
            onClick={() => go(n.id)}
            data-testid={`lds-nav-${n.id}`}
          >
            {n.label}
          </button>
        ))}
      </div>
    </nav>
  );
}

function ChallengeSection() {
  const c = LDS_CHALLENGE;
  return (
    <section className="cs-section cs-section-alt" id="challenge" data-testid="lds-challenge">
      <div className="cs-container">
        <Reveal>
          <span className="cs-kicker">{c.kicker}</span>
          <h2 className="cs-h2">{c.title}</h2>
        </Reveal>
        <Reveal delay={90} className="cs-why">{c.lead}</Reveal>
        <Reveal delay={120}>
          {c.copy.map((p) => (
            <p className="cs-lead" key={p.slice(0, 24)} style={{ marginTop: 0, marginBottom: 18 }}>{p}</p>
          ))}
        </Reveal>
        <div className="cs-challenge-grid" style={{ marginTop: 34 }}>
          {c.points.map((item, i) => {
            const Icon = ICONS[item.icon];
            return (
              <Reveal className="cs-challenge-card" key={item.title} delay={i * 70}>
                <span className="cs-x-icon">{Icon && <Icon size={22} />}</span>
                <h3 className="cs-card-title">{item.title}</h3>
                <p className="cs-card-copy">{item.copy}</p>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function GlanceSection() {
  const g = LDS_GLANCE;
  return (
    <section className="cs-section" id="glance" data-testid="lds-glance">
      <div className="cs-container">
        <Reveal>
          <span className="cs-kicker">{g.kicker}</span>
          <h2 className="cs-h2">{g.title}</h2>
        </Reveal>
        <div className="cs-tiles">
          {g.tiles.map((t, i) => (
            <Reveal className="cs-tile" key={t.label} delay={i * 60} data-testid={`lds-tile-${i}`}>
              <div className="cs-tile-value">{t.value}</div>
              <div className="cs-tile-label">{t.label}</div>
              {t.editable && <span className="cs-tile-edit">editable</span>}
            </Reveal>
          ))}
        </div>
        <Reveal delay={120} className="cs-note" as="p">{g.note}</Reveal>
      </div>
    </section>
  );
}

function Callout({ data }) {
  return (
    <Reveal className="cs-callout" data-testid="lds-callout">
      <span className="cs-callout-tag">{data.tag}</span>
      <Quote className="cs-callout-mark" size={40} strokeWidth={0} fill="currentColor" />
      <p className="cs-callout-text">{data.quote}</p>
      <div className="cs-callout-cite">{data.cite}</div>
    </Reveal>
  );
}

function Shots({ items }) {
  const openLightbox = useContext(LightboxContext);
  const norm = items.map((it) => (typeof it === 'string' ? { caption: it } : it));
  const filled = norm.some((i) => i.src);
  const cls = !filled ? '' : norm.length === 1 ? 'cs-shots-solo' : 'cs-shots-gallery';
  return (
    <div className={`cs-shots ${cls}`}>
      {norm.map((it, i) => (
        <Reveal className={`cs-shot ${it.src ? 'cs-shot-filled' : ''}`} key={it.caption} delay={i * 60}>
          <div className="cs-shot-chrome"><i /><i /><i /></div>
          {it.src ? (
            <figure className="cs-shot-fig">
              <img
                src={it.src}
                alt={it.caption}
                loading="lazy"
                className="cs-shot-zoom"
                role="button"
                tabIndex={0}
                data-testid="lds-shot-image"
                onClick={() => openLightbox && openLightbox({ src: it.src, caption: it.caption })}
                onKeyDown={(e) => {
                  if ((e.key === 'Enter' || e.key === ' ') && openLightbox) {
                    e.preventDefault();
                    openLightbox({ src: it.src, caption: it.caption });
                  }
                }}
              />
              <figcaption className="cs-shot-figcap">{it.caption}</figcaption>
            </figure>
          ) : (
            <div className="cs-shot-body">
              {/* screenshot: {it.caption} */}
              <ImageIcon size={26} />
              <span className="cs-shot-cap">{it.caption}</span>
            </div>
          )}
        </Reveal>
      ))}
    </div>
  );
}

function Lightbox({ data, onClose }) {
  useEffect(() => {
    if (!data) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => { document.removeEventListener('keydown', onKey); document.body.style.overflow = ''; };
  }, [data, onClose]);
  if (!data) return null;
  return (
    <div className="cs-lightbox" onClick={onClose} data-testid="lds-lightbox">
      <button type="button" className="cs-lightbox-close" onClick={onClose} aria-label="Close" data-testid="lds-lightbox-close">
        <X size={22} />
      </button>
      <figure className="cs-lightbox-fig" onClick={(e) => e.stopPropagation()}>
        <img src={data.src} alt={data.caption} />
        <figcaption>{data.caption}</figcaption>
      </figure>
    </div>
  );
}

function FeatureSection({ data }) {
  return (
    <section className="cs-section" id={data.id} data-testid={`lds-${data.id}`}>
      <div className="cs-container">
        <Reveal>
          <span className="cs-kicker">{data.kicker}</span>
          <h2 className="cs-h2">{data.title}</h2>
        </Reveal>
        <Reveal delay={90} className="cs-why">{data.why}</Reveal>

        <div className="cs-blocks">
          {data.blocks.map((b, i) => {
            const Icon = ICONS[b.icon];
            return (
              <Reveal className="cs-block" key={b.heading} delay={(i % 2) * 70}>
                <div className="cs-block-head">
                  <span className="cs-block-icon">{Icon && <Icon size={19} />}</span>
                  <h3 className="cs-block-heading">{b.heading}</h3>
                </div>
                <ul className="cs-block-list">
                  {b.points.map((p) => <li key={p.slice(0, 28)}><Check size={15} /> <span>{p}</span></li>)}
                </ul>
              </Reveal>
            );
          })}
        </div>

        {data.shots && data.shots.length > 0 && <Shots items={data.shots} />}

        {data.callouts && data.callouts.map((co) => <Callout key={co.cite} data={co} />)}
      </div>
    </section>
  );
}

function IntegrationsSection() {
  const d = LDS_INTEGRATIONS;
  const initials = (name) => name.replace(/\(.*?\)/g, '').trim().split(/\s+/).map((w) => w[0]).join('').slice(0, 2).toUpperCase();
  return (
    <section className="cs-section cs-section-alt" id="integrations" data-testid="lds-integrations">
      <div className="cs-container">
        <Reveal>
          <span className="cs-kicker">{d.kicker}</span>
          <h2 className="cs-h2">{d.title}</h2>
        </Reveal>
        <Reveal delay={90} className="cs-why">{d.why}</Reveal>
        <div className="cs-int-grid">
          {d.items.map((it, i) => (
            <Reveal className="cs-int-card" key={it.name} delay={(i % 4) * 50}>
              <span className="cs-int-logo" aria-hidden="true">{initials(it.name)}</span>
              <div>
                <div className="cs-int-name">{it.name}</div>
                <div className="cs-int-line">{it.line}</div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function EngineeringSection() {
  const d = LDS_ENGINEERING;
  return (
    <section className="cs-section" id="engineering" data-testid="lds-engineering">
      <div className="cs-container">
        <Reveal>
          <span className="cs-kicker">{d.kicker}</span>
          <h2 className="cs-h2">{d.title}</h2>
        </Reveal>
        <Reveal delay={90} className="cs-why">{d.why}</Reveal>
        <div className="cs-blocks">
          {d.blocks.map((b, i) => {
            const Icon = ICONS[b.icon];
            return (
              <Reveal className="cs-block" key={b.heading} delay={(i % 2) * 70}>
                <div className="cs-block-head">
                  <span className="cs-block-icon">{Icon && <Icon size={19} />}</span>
                  <h3 className="cs-block-heading">{b.heading}</h3>
                </div>
                <ul className="cs-block-list">
                  {b.points.map((p) => <li key={p.slice(0, 28)}><Check size={15} /> <span>{p}</span></li>)}
                </ul>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function ResultsSection() {
  const r = LDS_RESULTS;
  return (
    <section className="cs-section cs-section-alt" id="results" data-testid="lds-results">
      <div className="cs-container">
        <Reveal>
          <span className="cs-kicker">{r.kicker}</span>
          <h2 className="cs-h2">{r.title}</h2>
        </Reveal>
        <Reveal delay={90} className="cs-why">{r.lead}</Reveal>
        <div className="cs-tiles">
          {r.stats.map((t, i) => (
            <Reveal className="cs-tile" key={t.label} delay={i * 60}>
              <div className="cs-tile-value">{t.value}</div>
              <div className="cs-tile-label">{t.label}</div>
              {t.editable && <span className="cs-tile-edit">editable</span>}
            </Reveal>
          ))}
        </div>
        <div className="cs-outcomes">
          {r.outcomes.map((o, i) => (
            <Reveal className="cs-outcome" key={o.slice(0, 24)} delay={i * 70}>
              <Check size={16} /> <span>{o}</span>
            </Reveal>
          ))}
        </div>
        <Reveal delay={120} className="cs-note" as="p">{r.note}</Reveal>
      </div>
    </section>
  );
}

function ClosingCta() {
  const c = LDS_CTA;
  return (
    <section className="cs-cta-section" data-testid="lds-cta">
      <div className="cs-container">
        <Reveal>
          <h2 className="cs-cta-title">{c.title}</h2>
          <p className="cs-cta-sub">{c.sub}</p>
          <div className="cs-cta-btns">
            <a className="cs-btn cs-btn-primary" href={`mailto:${c.email}`} data-testid="lds-cta-email">
              Start a project <ArrowUpRight size={18} />
            </a>
            <a className="cs-btn cs-btn-ghost" href={c.siteUrl} target="_blank" rel="noopener noreferrer" data-testid="lds-cta-site">
              Visit boostmarketing.agency <ExternalLink size={16} />
            </a>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function CaseStudyFooter() {
  return (
    <footer className="cs-footer" data-testid="lds-footer">
      <div className="cs-container cs-footer-inner">
        <div className="cs-footer-brand">
          <img src={BOOST_LOGO} alt="Boost! Design & Marketing" />
          <div className="cs-footer-credit">
            Designed &amp; built by <strong>Boost! Design &amp; Marketing Ltd</strong>
            <br />© {new Date().getFullYear()} — Giving your brand lift-off.
          </div>
        </div>
        <a className="cs-footer-link" href="https://www.boostmarketing.agency" target="_blank" rel="noopener noreferrer" data-testid="lds-footer-link">
          www.boostmarketing.agency <ArrowUpRight size={15} />
        </a>
      </div>
    </footer>
  );
}

export default function CaseStudyLondonDrumsticks() {
  const [lightbox, setLightbox] = useState(null);
  useSeo({
    title: 'London Drumstick Co. Case Study — Bespoke E-commerce & Operations Platform | Boost!',
    description:
      'How Boost! designed and built a bespoke e-commerce and business-operations platform for London Drumstick Co. — a direct-to-drummer store, custom stick builder and full back-office, built as one system.',
    canonical: CANONICAL,
    image: LDS_OG_IMAGE,
    jsonLd: SEO_JSONLD,
  });
  return (
    <LightboxContext.Provider value={setLightbox}>
      <div className="cs-page cs-drumstick" data-testid="case-study-london-drumsticks">
        <div className="cs-grain" />
        <div className="cs-glows" />
        <ScrollProgress />
        <Link to="/work" className="cs-back" data-testid="lds-back-to-work">
          <ArrowLeft size={16} /> Back to work
        </Link>
        <main>
          <Hero />
          <SectionNav />
          <ChallengeSection />
          <GlanceSection />
          {LDS_SECTIONS.map((s) => <FeatureSection key={s.id} data={s} />)}
          <IntegrationsSection />
          <EngineeringSection />
          <ResultsSection />
          <ClosingCta />
          <RelatedJournal caseStudy="london-drumstick-co" />
          <CaseStudyNav
            to="/case-studies/temple-of-sound"
            title="Two legends, one house"
            client="Temple of Sound · Bespoke full-stack retail platform"
          />
        </main>
        <CaseStudyFooter />
        <BackToTop />
        <Lightbox data={lightbox} onClose={() => setLightbox(null)} />
      </div>
    </LightboxContext.Provider>
  );
}
